import React, { useCallback, useEffect, useState, useRef } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { PageTitle, Loading } from '../root/components/common'
import { useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import * as actions from './actions'
import debounce from 'lodash/debounce'
import { ReportsFilters } from '../root/components/common/reports-filters'
import { useReportsFilters } from '../root/hooks/useReportsFilters'
import { ReportsTable } from '../root/components/common/reports-table/reports-table'
import { isEmpty, isNull } from 'lodash'
import {
    urlToSearchState,
    useSyncUrlState
} from '../../utils/hooks/useSyncUrlState'
import { OptionSwitcher } from '../root/components/common/option-switcher/option-switcher'
import { REPRESENTATION } from '../root/constants'
import { UserPermissions } from '../../services/permissions'
import { LOCAL_LOCALE_KEY } from '../root/constants'
import classNames from 'classnames'
import { getFileForExport } from './api/report-api'
import { downloadFile } from 'web/src/utils'
import { enqueueSnackbar } from '../root/actions'
import {
    composeFileName,
    isFiltersTimeRangeExceededForExport
} from '../../utils'
import { ExportMenu } from '../root/components/common/export-menu'

const useStyles = makeStyles(theme => ({
    switcher: {
        marginRight: 12
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 24
    },
    wrapper: {
        height: 'calc(100% - 70px)'
    },
    nameLink: {
        color: theme.textPrimary,
        textDecoration: 'none',
        fontWeight: 'bold',
        cursor: 'pointer'
    },
    actions: {
        display: 'flex',
        justifyContent: 'center'
    },
    icon: {
        color: theme.primary,
        cursor: 'pointer'
    },
    batteryWrapper: {
        display: 'flex',
        justifyContent: 'center'
    },
    statusWrapper: {
        display: 'flex',
        justifyContent: 'center'
    },
    modalButtons: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: 15
    },
    modalButton: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: 15
    },
    pickerWrapper: {
        marginTop: 15
    },
    fromDate: {
        marginRight: 16
    },
    input: {
        fontSize: 14,
        height: '32px',
        width: 150,
        borderRadius: '4px'
    },
    title: {
        flexGrow: 1,
        marginBottom: 0
    },
    filterSelect: {
        marginRight: 16
    },
    statusFilterItem: {
        fontSize: 14
    },
    placeholderFilter: {
        color: theme.palette.text.secondary
    },
    exportBtnsWrapper: {
        display: 'flex',
        gap: '6px',
        marginRight: '16px'
    },
    loader: {
        top: '50vh',
        zIndex: 9,
        position: 'absolute',
        left: 0,
        right: 0,
        marginLeft: 'auto',
        marginRight: 'auto'
    },
    disabledWrapper: {
        opacity: 0.3,
        pointerEvents: 'none'
    },
    flex: {
        display: 'flex'
    }
}))

export default function Reports() {
    const classes = useStyles()
    const fromShakeableRef = useRef(null)
    const { isLoading, locale } = useSelector(state => state.main)
    const { isLoadingMore } = useSelector(state => state.reports)
    const { reports } = useSelector(state => state.reports)
    const [isExporting, setIsExporting] = useState(false)
    const dispatch = useDispatch()
    const location = useLocation()
    const { filters, d, isLoadingFilters } = useReportsFilters(
        urlToSearchState(location),
        UserPermissions.isAllOriginalGranted()
    )

    useSyncUrlState(filters)

    const loadMoreReports = useCallback(
        debounce(() => {
            if (!isLoadingFilters) {
                dispatch(actions.fetchMoreReports(filters))
            }
        }, 300)
    )

    const exportReports = async fileType => {
        if (isFiltersTimeRangeExceededForExport(filters)) {
            fromShakeableRef.current.shake()
            dispatch(
                enqueueSnackbar({
                    message: locale.reports.exportDenyMsg,
                    options: {
                        variant: 'warning'
                    }
                })
            )
            return
        }
        setIsExporting(true)
        const locale_key = localStorage.getItem(LOCAL_LOCALE_KEY)
        const params = { fileType, locale_key, ...filters }
        const blob = await getFileForExport(params)
        if (blob) {
            const fileName = composeFileName('reports', fileType)
            downloadFile(blob, fileType, fileName)
        } else {
            dispatch(
                enqueueSnackbar({
                    message: locale.sagasMsgs.getDeviceReportsEx,
                    options: {
                        variant: 'error'
                    }
                })
            )
        }
        setIsExporting(false)
    }

    useEffect(() => {
        if (!isLoadingFilters) {
            dispatch(actions.reportsRequest(filters))
        }
    }, [filters, isLoadingFilters])

    return (
        <>
            {isExporting && (
                <div className={classes.loader}>
                    <Loading />
                </div>
            )}
            <div
                className={classNames(classes.wrapper, {
                    [`${classes.disabledWrapper}`]: isExporting
                })}>
                <div className={classes.header}>
                    <PageTitle
                        className={classes.title}
                        title={locale.reports.reports}
                    />
                    <div className={classes.exportBtnsWrapper}>
                        <ExportMenu>
                            {({ handleClose }) => (
                                <>
                                    <ExportMenu.Csv
                                        locale={locale}
                                        onExport={fileType => {
                                            exportReports(fileType)
                                            handleClose()
                                        }}
                                    />
                                    <ExportMenu.Excel
                                        locale={locale}
                                        onExport={fileType => {
                                            exportReports(fileType)
                                            handleClose()
                                        }}
                                    />
                                    <ExportMenu.Pdf
                                        locale={locale}
                                        onExport={fileType => {
                                            exportReports(fileType)
                                            handleClose()
                                        }}
                                    />
                                </>
                            )}
                        </ExportMenu>
                    </div>
                    {UserPermissions.isAllOriginalGranted() && (
                        <OptionSwitcher
                            className={classes.switcher}
                            options={{
                                [REPRESENTATION.all]: locale.reports.allReports,
                                [REPRESENTATION.assigned]:
                                    locale.devices.assigned
                            }}
                            selected={
                                filters.is_assigned
                                    ? REPRESENTATION.assigned
                                    : REPRESENTATION.all
                            }
                            onChange={value =>
                                d({
                                    type: 'is_assigned',
                                    value: value === REPRESENTATION.assigned
                                })
                            }
                        />
                    )}
                    <ReportsFilters
                        onChange={d}
                        filters={filters}
                        fromShakeableRef={fromShakeableRef}
                    />
                </div>

                {isLoading && isNull(reports) ? <Loading /> : null}

                {!isLoading && (!reports || reports.length === 0) && (
                    <div>{locale.reports.noReportsForRange}</div>
                )}

                {!isEmpty(reports) && (
                    <ReportsTable
                        isLoading={isLoading}
                        sorting={filters.sort_by}
                        onSort={sort_by => d({ type: 'sort', sort_by })}
                        reports={reports}
                        isLoadingMore={isLoadingMore}
                        onLoadMore={loadMoreReports}
                        showAssignmentColumn
                    />
                )}
            </div>
        </>
    )
}
